import React from "react";
import { getClassName } from "@sscs/utils";
import styles from "./section.module.scss";
/** The section heading on a trailing hairline rule (the fieldset-legend idiom). The rule is a flex-filling span, not a border, which would underline the label too. */
const Title = ({ as: Heading = "h3", className, action, children, }) => (React.createElement("div", { className: getClassName(styles["title-row"], className) },
    React.createElement(Heading, { className: styles["section-title"] }, children),
    React.createElement("span", { className: styles.rule, "aria-hidden": true }),
    action && (React.createElement("div", { className: getClassName(styles["section-action"], action.props.className) }, action))));
/** Fainter copy under the title. Dialog intro text lives here, in the first Section. */
const Description = ({ className, children }) => (React.createElement("p", { className: getClassName(styles["section-description"], className) }, children));
/** A control hoisted into the title row, flush right. A `className` here lands on the `.section-action` wrapper. */
const Action = ({ children }) => React.createElement(React.Fragment, null, children);
/** The section's fields, stacked on one gap. The `owns-field-rhythm` marker zeroes the intrinsic field margins so the gap is the whole rhythm. */
const Content = ({ className, children }) => (React.createElement("div", { className: getClassName(styles["section-content"], "owns-field-rhythm", className) }, children));
const isElementOfType = (node, type) => React.isValidElement(node) && node.type === type;
/**
 * A titled content group INSIDE an island: title row → description → content. Not a card — no border,
 * fill, radius or padding of its own, which makes it the answer to a card nested in a card. The title
 * is optional; without one it is a pure grouping container and draws no rule. Exposed as both
 * `Section` and `Dialog.Section`, the same component under two names.
 */
const Section = ({ className, children }) => {
    const childList = React.Children.toArray(children);
    const action = childList.find((child) => isElementOfType(child, Action));
    const titled = childList.some((child) => isElementOfType(child, Title));
    return (React.createElement("section", { className: getClassName(styles.section, className) },
        action && !titled && (React.createElement("div", { className: styles["title-row"] },
            React.createElement("span", { className: styles.rule, "aria-hidden": true }),
            React.createElement("div", { className: getClassName(styles["section-action"], action.props.className) }, action))),
        childList
            .filter((child) => child !== action)
            .map((child) => action && isElementOfType(child, Title)
            ? React.cloneElement(child, { action })
            : child)));
};
Section.Title = Title;
Section.Description = Description;
Section.Action = Action;
Section.Content = Content;
export { Title, Description, Action, Content };
export default Section;
//# sourceMappingURL=Section.js.map